import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Chip,
  IconButton,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { Task, TaskStatus, SnackbarState } from '../../types/todoTypes';
import { updateTask } from '../../services/todoService';
import { getStatusColor, formatTimestamp, validateTitle, validateDescription } from '../../utils/todoUtils';

interface TaskDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  task: Task | null;
  onTaskUpdated: () => void;
  onDeleteRequested: (task: Task) => void;
  setSnackbar: (snackbar: SnackbarState) => void;
}

const TaskDetailsDialog: React.FC<TaskDetailsDialogProps> = ({
  open,
  onClose,
  task,
  onTaskUpdated,
  onDeleteRequested,
  setSnackbar,
}) => {
  const [editMode, setEditMode] = useState(false);
  const [editedTitle, setEditedTitle] = useState('');
  const [editedDescription, setEditedDescription] = useState('');
  const [editedStatus, setEditedStatus] = useState<TaskStatus>('Pending');
  const [loading, setLoading] = useState(false);

  const [titleError, setTitleError] = useState('');
  const [descriptionError, setDescriptionError] = useState('');

  // Initialize form when task changes
  useEffect(() => {
    if (task) {
      setEditedTitle(task.title);
      setEditedDescription(task.description);
      setEditedStatus(task.status);
      setEditMode(false);
      setTitleError('');
      setDescriptionError('');
    }
  }, [task]);

  const handleClose = () => {
    setEditMode(false);
    onClose();
  };

  const handleEditToggle = () => {
    if (editMode) {
      // Cancel editing - reset to original values
      if (task) {
        setEditedTitle(task.title);
        setEditedDescription(task.description);
        setEditedStatus(task.status);
        setTitleError('');
        setDescriptionError('');
      }
    }
    setEditMode(!editMode);
  };

  const handleSave = async () => {
    if (!task) return;

    // Validate inputs
    const titleValidation = validateTitle(editedTitle);
    const descriptionValidation = validateDescription(editedDescription);

    setTitleError(titleValidation.error || '');
    setDescriptionError(descriptionValidation.error || '');

    if (!titleValidation.valid || !descriptionValidation.valid) {
      return;
    }

    setLoading(true);

    try {
      await updateTask(task.id, {
        title: editedTitle.trim(),
        description: editedDescription.trim(),
        status: editedStatus,
      });

      setSnackbar({
        open: true,
        message: 'Task updated successfully',
        severity: 'success',
      });

      setEditMode(false);
      onTaskUpdated();
    } catch (error) {
      console.error('Error updating task:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update task',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = () => {
    if (task) {
      onDeleteRequested(task);
      handleClose();
    }
  };

  if (!task) {
    return null;
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Task Details</Typography>
          <Box>
            <IconButton
              onClick={handleEditToggle}
              color={editMode ? 'default' : 'primary'}
              disabled={loading}
            >
              {editMode ? <CloseIcon /> : <EditIcon />}
            </IconButton>
            <IconButton onClick={handleClose} disabled={loading}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Title */}
          {editMode ? (
            <TextField
              label="Task Title"
              fullWidth
              value={editedTitle}
              onChange={(e) => {
                setEditedTitle(e.target.value);
                setTitleError('');
              }}
              error={!!titleError}
              helperText={titleError}
              autoFocus
            />
          ) : (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Title
              </Typography>
              <Typography variant="h6" sx={{ mt: 0.5 }}>
                {task.title}
              </Typography>
            </Box>
          )}

          {/* Description */}
          {editMode ? (
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={6}
              value={editedDescription}
              onChange={(e) => {
                setEditedDescription(e.target.value);
                setDescriptionError('');
              }}
              error={!!descriptionError}
              helperText={descriptionError}
            />
          ) : (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Description
              </Typography>
              <Typography variant="body1" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                {task.description}
              </Typography>
            </Box>
          )}

          {/* Status */}
          {editMode ? (
            <FormControl fullWidth>
              <InputLabel id="status-edit-label">Status</InputLabel>
              <Select
                labelId="status-edit-label"
                value={editedStatus}
                onChange={(e) => setEditedStatus(e.target.value as TaskStatus)}
                label="Status"
              >
                <MenuItem value="Pending">Pending</MenuItem>
                <MenuItem value="In Progress">In Progress</MenuItem>
                <MenuItem value="Blocked">Blocked</MenuItem>
                <MenuItem value="Complete">Complete</MenuItem>
              </Select>
            </FormControl>
          ) : (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Status
              </Typography>
              <Box sx={{ mt: 0.5 }}>
                <Chip label={task.status} color={getStatusColor(task.status)} />
              </Box>
            </Box>
          )}

          <Divider />

          {/* Metadata */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Created
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {formatTimestamp(task.createdAt)}
              </Typography>
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary">
                Last Edited
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {task.lastEditedAt ? formatTimestamp(task.lastEditedAt) : 'Never'}
              </Typography>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
        <Button
          onClick={handleDelete}
          color="error"
          startIcon={<DeleteIcon />}
          disabled={loading}
        >
          Delete Task
        </Button>

        <Box sx={{ display: 'flex', gap: 1 }}>
          {editMode ? (
            <>
              <Button onClick={handleEditToggle} color="inherit" disabled={loading}>
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                color="primary"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : 'Save Changes'}
              </Button>
            </>
          ) : (
            <Button onClick={handleClose} color="primary" variant="contained">
              Close
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default TaskDetailsDialog;
